import React, { useState } from 'react';
import {Handle, Node, NodeTypes, Position} from 'reactflow';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import 'reactflow/dist/style.css';
import {AccessControl, ApprovalConfig, RequiredField} from "@/modules/action-flow/config";

export interface Action {
    name: string;
    label: string;
    event?: string;
    nextNode?: string;  // New field for REROUTE actions
}


// Updated NodeData interface in nodes.tsx
export interface DecisionRule {
    expression: string;
    event: string;
    nextNode: string;
}

export interface NodeData {
    label: string;
    type: string;
    description?: string;
    actions?: Action[];
    decisionRules?: DecisionRule[]; // New field for decision nodes
    defaultEvent?: string; // Default event for decision nodes
    defaultNextNode?: string; // Default next node for decision nodes
    level: number;

    // Validator specific fields
    accessControls?: AccessControl[];
    requirements?: RequiredField[];
    approvalConfig?: ApprovalConfig;

    // Notification configurations
    notificationConfigurations?: NotificationConfigurationSelection[];

    // Executor specific fields
    beanName?: string;
    transitions?: any[];
}




export type CustomNode = Node<NodeData>;

interface NodeWrapperProp {
    data: NodeData;
    color: string;
    children: React.ReactNode;
}

const NodeWrapper: React.FC<NodeWrapperProp> = ({ data, color, children }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const getColor = (baseColor: string) => {
        switch (baseColor) {
            case 'gray': return '#4B5563';
            case 'yellow': return '#EAB308';
            case 'purple': return '#8B5CF6';
            case 'blue': return '#3B82F6';
            case 'orange': return '#F97316';
            case 'red': return '#EF4444';
            case 'green': return '#10B981';
            default: return '#4B5563'; // default to gray
        }
    };

    const nodeStyle = {
        backgroundColor: getColor(color),
        padding: '1rem',
        borderRadius: '0.5rem',
        color: 'white',
        position: 'relative' as const,
        minWidth: '150px',
        minHeight: '50px',
    };

    return (
        <div style={nodeStyle}>
            <Handle type="target" position={Position.Top} />
            {children}
            <Tooltip
                title={data.description}
                open={showTooltip}
                onOpenChange={setShowTooltip}
            >
                <InfoCircleOutlined
                    style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        cursor: 'pointer',
                    }}
                    onClick={() => setShowTooltip(!showTooltip)}
                />
            </Tooltip>
            <Handle type="source" position={Position.Bottom} />
        </div>
    );
};

export const MakerNode: React.FC<{ data: NodeData }> = ({ data }) => (
    <NodeWrapper data={data} color="gray">
        <h3 className="font-bold">{data.label}</h3>
    </NodeWrapper>
);

export const ValidatorNode: React.FC<{ data: NodeData }> = ({ data }) => (
    <NodeWrapper data={data} color="yellow">
        <h3 className="font-bold">{data.label}</h3>
    </NodeWrapper>
);

export const RouterNode: React.FC<{ data: NodeData }> = ({ data }) => (
    <NodeWrapper data={data} color="purple">
        <h3 className="font-bold">{data.label}</h3>
    </NodeWrapper>
);

export const ExecutorNode: React.FC<{ data: NodeData }> = ({ data }) => (
    <NodeWrapper data={data} color={"blue"}>
        <h3 className="font-bold">{data.label}</h3>
    </NodeWrapper>
);

export const TimerNode: React.FC<{ data: NodeData }> = ({ data }) => (
    <NodeWrapper data={data} color="orange">
        <h3 className="font-bold">{data.label}</h3>
    </NodeWrapper>
);

export const AlerterNode: React.FC<{ data: NodeData }> = ({ data }) => (
    <NodeWrapper data={data} color="red">
        <h3 className="font-bold">{data.label}</h3>
    </NodeWrapper>
);

export const RejectedNode: React.FC<{ data: NodeData }> = ({ data }) => (
    <NodeWrapper data={data} color="orange">
        <h3 className="font-bold">REJECTED</h3>
    </NodeWrapper>
);

export const ApprovedNode: React.FC<{ data: NodeData }> = ({ data }) => (
    <NodeWrapper data={data} color="green">
        <h3 className="font-bold">APPROVED</h3>
    </NodeWrapper>
);

export const nodeTypes: NodeTypes = {
    maker: MakerNode,
    validator: ValidatorNode,
    router: RouterNode,
    executor: ExecutorNode,
    timer: TimerNode,
    alerter: AlerterNode,
    rejected: RejectedNode,
    approved: ApprovedNode
};

export interface NotificationConfigurationSelection {
    code: string;  // Using code instead of id for portability
    name: string;  // Display name for UI purposes
    platform: string; // EMAIL, SMS, WHATS_APP, etc.
    active: boolean;
}

export interface NodeEditorProps {
    node: CustomNode;
    updateNode: (id: string, data: Partial<NodeData>) => void;
}

export interface Transition {
    from: string;
    to: string;
    event: string;
    nextNode?: string;
    condition?: string;
    validatorIndex?: number;
}

export interface ViewportState {
    x:number;
    y:number;
    zoom:number;
}

export interface WorkflowConfig {
    workflowId: string;
    name: string;
    description: string;
    version: string;
    states: CustomNode[];
    transitions?: Transition[];
    viewportState?: ViewportState;

}

export const formatNodeId = (label: string): string => {
    return label.toUpperCase().replace(/\s+/g, '_');
};